import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguageStore } from "@/stores/languageStore";

interface OnboardingStep {
  title: string;
  titleAm: string;
  description: string;
  descriptionAm: string;
  icon: string;
}

const STEPS: OnboardingStep[] = [
  {
    title: "Welcome to SARTN",
    titleAm: "ወደ SARTN እንኳን ደህና መጡ",
    description: "Your AI-powered Telegram business assistant. Automate everything.",
    descriptionAm: "በ AI የሚሰራ የቴሌግራም ንግድ ረዳት። ሁሉንም ነገር አውቶሜት ያድርጉ።",
    icon: "M13 10V3L4 14h7v7l9-11h-7z",
  },
  {
    title: "Manage Contacts",
    titleAm: "ግንኙነቶችን ያስተዳድሩ",
    description: "Organize customers, track orders, and never forget a conversation.",
    descriptionAm: "ደንበኞችን ያደራጁ፣ ትዕዛዞችን ይከታተሉ፣ ምንም ውይይት አይርሱ።",
    icon: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  },
  {
    title: "Automate Sales",
    titleAm: "ሽያጮችን አውቶሜት ያድርጉ",
    description: "Set up auto-replies, campaigns, and let AI handle customer conversations.",
    descriptionAm: "ራስ-ሰር ምላሾችን፣ ዘመቻዎችን ያዘጋጁ፣ AI ደንበኞችን ይያዝ።",
    icon: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  },
  {
    title: "Track Everything",
    titleAm: "ሁሉንም ይከታተሉ",
    description: "Analytics, market prices, competitor intelligence — all in one place.",
    descriptionAm: "ትንተና፣ የገበያ ዋጋዎች፣ ተፎካካሪ መረጃ — ሁሉም በአንድ ቦታ።",
    icon: "M3 3v18h18M18.7 8l-5.1 5.2-2.8-2.7L7 14.3",
  },
];

interface OnboardingTutorialProps {
  onComplete: () => void;
}

export function OnboardingTutorial({ onComplete }: OnboardingTutorialProps) {
  const language = useLanguageStore((state) => state.language);
  const [currentStep, setCurrentStep] = useState(0);
  const step = STEPS[currentStep]!;
  const isLastStep = currentStep === STEPS.length - 1;
  const title = language === "am" ? step.titleAm : step.title;
  const description = language === "am" ? step.descriptionAm : step.description;

  function handleNext() {
    if (isLastStep) {
      localStorage.setItem("sartn_onboarding_complete", "true");
      onComplete();
    } else {
      setCurrentStep((s) => s + 1);
    }
  }

  function handleSkip() {
    localStorage.setItem("sartn_onboarding_complete", "true");
    onComplete();
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[var(--color-bg)]">
      <div className="flex justify-end p-4">
        <button
          onClick={handleSkip}
          className="text-xs text-[var(--color-text-muted)] active:text-[var(--color-text-muted)]"
        >
          {language === "am" ? "ዝለል" : "Skip"}
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center"
          >
            <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-3xl bg-blue-50">
              <svg className="h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={step.icon} />
              </svg>
            </div>

            <h2 className="mb-2 text-center text-xl font-bold text-[var(--color-text)]">
              {title}
            </h2>
            <p className="max-w-xs text-center text-sm text-[var(--color-text-secondary)]">
              {description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex flex-col items-center gap-4 px-8 pb-12">
        <div className="flex gap-2">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === currentStep ? "w-6 bg-blue-600" : "w-2 bg-[var(--color-border)]"
              }`}
            />
          ))}
        </div>

        <button
          onClick={handleNext}
          className="w-full rounded-xl bg-blue-600 py-3.5 text-sm font-semibold text-white active:bg-blue-700"
        >
          {language === "am" ? (isLastStep ? "ጀምር" : "ቀጣይ") : isLastStep ? "Get Started" : "Next"}
        </button>
      </div>
    </div>
  );
}
